const fs = require('fs');
const path = require('path');
const i18n = require('i18n');
const { AppError } = require('./AppError');

const DEFAULT_LOCALE = 'en';

/**
 * Statically-configured default directory where the translations files
 * are stored, from the root of the Node process.
 *
 * @type {string}
 */
let translationDirectoryPathDefault = 'i18n';

/**
 * Transforms errors from the "ErrorCatalog" format into an AppError object.
 *
 * # Display Message
 *
 * Functions in this take optional "translationDirectory" and "lang"
 * arguments, which are used to add a "displayMessage" to the converted
 * AppError.
 *
 * The "displayMessage" is a message which is intended to be seen by
 * the user, and is expected to be shown as an error message in
 * the client application.
 *
 * #### Example translation files:
 * - "/i18n/en.json"
 * - "/i18n/fr.json"
 * - "/i18n/ar.json"
 *
 * ### Error Code Mapping
 *
 * Inside the language files, there should be a "errorCodes" property,
 * which maps error codes to their corresponding displayMessage,
 *
 * #### Example translation file contents:
 * ```
 * {
 *   "errorCodes": {
 *     "ERRORS-LIB-01": "My translated message 01 EN",
 *     "NPM-LIB-02": "My translated message 02 EN",
 *     "NPM-LIB-03": "My translated message 03 EN"
 *   }
 * }
 * ```
 */
class ErrorTransformer {

  /**
   * Translates the error into the requested language.
   * If the error is not an AppError, or if the language is not specified,
   * the error is returned as-is.
   * Otherwise, the error is translated into the requested language.
   *
   * @param {AppError|any} error
   * @param {string} lang
   * @return {AppError|any}
   */
  static translate({
    error,
    lang = DEFAULT_LOCALE,
  }) {
    if (!(error instanceof AppError) || !lang) {
      return error;
    }

    const i18nIsConfigured = this.configureI18n({ translationDirectory: translationDirectoryPathDefault });

    if (!i18nIsConfigured) {
      return error;
    }

    const { translatedErrorMessage, translatedErrorTitle } = this.findTranslatedDisplayText({
      error,
      lang,
      dynamicValues: error.dynamicValues || {},
    });

    if (translatedErrorMessage) {
      error.setDisplayMessage(translatedErrorMessage);
    }

    if (translatedErrorTitle) {
      error.setDisplayTitle(translatedErrorTitle);
    }

    return error;
  }

  /**
   * @param {CodifiedError} error
   * @param {string} lang
   * @param dynamicValues
   * @return {{translatedErrorMessage: (string|null), translatedErrorTitle: (string|null)}}
   */
  static findTranslatedDisplayText({ error, lang, dynamicValues }) {
    const translatedErrorMessage = this.findTranslatedDisplayMessage({
      error,
      lang,
      dynamicValues,
    });

    const translatedErrorTitle = this.findTranslatedDisplayTitle({
      error,
      lang,
      dynamicValues,
    });

    return {
      translatedErrorMessage,
      translatedErrorTitle,
    };
  }

  /**
   * @param error - ErrorCatalog entry.
   * @param details
   * @param originalError
   * @param {string} lang - ISO language code for the preferred translation language.
   * @param translationDirectory - Path from the root directory Node is running in to the directory containing i18n translation files.
   * @param {DynamicTranslationValues} dynamicValues
   *    "Moustache"-style values to replace in the raw translation text.
   *    Example:
   *    <br/> translationText: "Value should be between {{min}} and {{max}}"
   *    <br/> dynamicValues: {min: 1, max: 100}
   *
   * @return {AppError}
   */
  static format({
    error,
    details,
    originalError,
    lang = undefined,
    translationDirectory = translationDirectoryPathDefault,
    dynamicValues = {},
  }) {
    const errorDetails = {};

    // Rewrite context if is a function with params
    if (error.details instanceof Function) {
      errorDetails.details = error.details(details);
    }

    // Set an original error if defined
    if ((typeof originalError) !== 'undefined') {
      errorDetails.originalError = originalError;
    }

    if (lang) {

      const i18nIsConfigured = this.configureI18n({ translationDirectory });

      if (i18nIsConfigured) {
        const { translatedErrorMessage, translatedErrorTitle } = this.findTranslatedDisplayText({
          error,
          lang,
          dynamicValues: dynamicValues || {},
        });

        if (translatedErrorMessage) {
          errorDetails.displayMessage = translatedErrorMessage;
        }

        if (translatedErrorTitle) {
          errorDetails.displayTitle = translatedErrorTitle;
        }
      }
    }

    return new AppError({
      ...error,
      ...errorDetails,
    });
  }

  /**
   * @param {number} statusCode
   * @param {AppError} remoteErrorData
   * @param {Array<ErrorCatalogEntry>} remoteErrorCatalog
   * @param unknownError
   * @param {IsoLanguageCode} lang
   * @param {string} translationDirectory
   * @param {DynamicTranslationValues} dynamicValues
   * @return {AppError}
   */
  static formatRemote({
    statusCode,
    remoteErrorData,
    remoteErrorCatalog,
    unknownError,
    lang = undefined,
    translationDirectory = translationDirectoryPathDefault,
    dynamicValues = {},
  }) {
    const error = remoteErrorCatalog.find((catalogError) => catalogError.referenceStatusCode === statusCode);
    const defaultError = remoteErrorCatalog.find((catalogError) => catalogError.default);

    return this.format({
      error: error || defaultError || unknownError,
      details: remoteErrorData,
      originalError: remoteErrorData,
      lang,
      translationDirectory,
      dynamicValues,
    });
  }

  /**
   * @param {string} referenceErrorCode
   * @param remoteErrorData
   * @param {Array<ErrorCatalogEntry>} remoteErrorCatalog
   * @param unknownError
   * @param {IsoLanguageCode} lang
   * @param {string} translationDirectory
   * @param {DynamicTranslationValues} dynamicValues
   * @return {AppError}
   */
  static formatRemoteFromCode({
    referenceErrorCode,
    remoteErrorData,
    remoteErrorCatalog,
    unknownError,
    lang = undefined,
    translationDirectory = translationDirectoryPathDefault,
    dynamicValues = {},
  }) {
    const error = remoteErrorCatalog.find((catalogError) => catalogError.referenceErrorCode === referenceErrorCode);
    const defaultError = remoteErrorCatalog.find((catalogError) => catalogError.default);

    return this.format({
      error: error || defaultError || unknownError,
      details: remoteErrorData,
      originalError: remoteErrorData,
      lang,
      translationDirectory,
      dynamicValues,
    });
  }

  /**
   * @param {CodifiedError} error
   * @param {IsoLanguageCode} lang
   * @param {string} translationDirectory
   * @param {DynamicTranslationValues} dynamicValues
   * @return {string|null}
   */
  static findTranslatedDisplayMessage({
    error,
    lang = DEFAULT_LOCALE,
    dynamicValues = {},
  }) {

    return i18n.__({
      phrase: `errorCodes.${error.code}.message`,
      locale: lang,
    }, dynamicValues);
  }

  static findTranslatedDisplayTitle({
    error,
    lang = DEFAULT_LOCALE,
    dynamicValues = {},
  }) {

    return i18n.__({
      phrase: `errorCodes.${error.code}.title`,
      locale: lang,
    }, dynamicValues);
  }

  static reset() {
    this.configure({
      translationDirectory: 'i18n',
    });
  }

  static configure({
    translationDirectory,
  }) {
    if (translationDirectory) {
      translationDirectoryPathDefault = translationDirectory;
    }
  }

  static configureI18n({ translationDirectory }) {
    const directory = path.join(process.cwd(), translationDirectory);

    if (!fs.existsSync(directory)) {
      return null;
    }

    i18n.configure({
      defaultLocale: DEFAULT_LOCALE,
      directory,
      updateFiles: false,
      objectNotation: true,
      missingKeyFn: () => undefined,
    });

    return i18n;
  }
}

module.exports = {
  ErrorTransformer,
};
